import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "celebrationsToggle"]
  
  connect() {
    // Set initial state of celebrations toggle
    if (this.hasCelebrationsToggleTarget) {
      this.setupCelebrationsToggle()
    }
  }
  
  showModal() {
    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('flex')
    document.body.style.overflow = 'hidden'
    
    // Focus trap
    this.modalTarget.addEventListener('keydown', this.handleKeydown.bind(this))
  }
  
  hideModal() {
    this.modalTarget.classList.add('hidden')
    this.modalTarget.classList.remove('flex')
    document.body.style.overflow = 'auto'
    
    // Remove focus trap
    this.modalTarget.removeEventListener('keydown', this.handleKeydown.bind(this))
  }
  
  stopPropagation(event) {
    event.stopPropagation()
  }
  
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.hideModal()
    }
  }
  
  setupCelebrationsToggle() {
    this.celebrationsToggleTarget.addEventListener('change', (event) => {
      this.updateCelebrations(event.target.checked)
    })
  }
  
  async updateCelebrations(enabled) {
    try {
      const response = await fetch('/set_celebrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          celebrations: enabled
        })
      })
      
      if (!response.ok) {
        throw new Error('Failed to update celebrations setting')
      }
      
      // Optional: Show a brief success message
      this.showSuccessMessage('Celebrations setting updated')
      
    } catch (error) {
      console.error('Failed to update celebrations:', error)
      
      // Revert the toggle state if the request failed
      this.celebrationsToggleTarget.checked = !enabled
      
      // Optional: Show error message
      this.showErrorMessage('Failed to update celebrations setting')
    }
  }
  
  showSuccessMessage(message) {
    // Create toast success notification
    const toastId = 'toast-' + Date.now()
    const messageEl = document.createElement('div')
    messageEl.id = toastId
    messageEl.className = 'fixed top-4 right-4 z-[10000] flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow-sm dark:text-gray-400 dark:bg-gray-800'
    messageEl.setAttribute('role', 'alert')
    
    messageEl.innerHTML = `
      <div class="inline-flex items-center justify-center shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg dark:bg-green-800 dark:text-green-200">
        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
        </svg>
        <span class="sr-only">Check icon</span>
      </div>
      <div class="ms-3 text-sm font-normal">${message}</div>
      <button type="button" class="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700" aria-label="Close">
        <span class="sr-only">Close</span>
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
      </button>
    `
    
    document.body.appendChild(messageEl)
    
    // Add close button functionality
    const closeButton = messageEl.querySelector('button[aria-label="Close"]')
    closeButton.addEventListener('click', () => {
      messageEl.remove()
    })
    
    // Auto-remove after 3 seconds
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.remove()
      }
    }, 3000)
  }
  
  showErrorMessage(message) {
    // Create toast error notification
    const toastId = 'toast-error-' + Date.now()
    const messageEl = document.createElement('div')
    messageEl.id = toastId
    messageEl.className = 'fixed top-4 right-4 z-[10000] flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow-sm dark:text-gray-400 dark:bg-gray-800'
    messageEl.setAttribute('role', 'alert')
    
    messageEl.innerHTML = `
      <div class="inline-flex items-center justify-center shrink-0 w-8 h-8 text-red-500 bg-red-100 rounded-lg dark:bg-red-800 dark:text-red-200">
        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
        </svg>
        <span class="sr-only">Error icon</span>
      </div>
      <div class="ms-3 text-sm font-normal">${message}</div>
      <button type="button" class="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700" aria-label="Close">
        <span class="sr-only">Close</span>
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
      </button>
    `
    
    document.body.appendChild(messageEl)
    
    // Add close button functionality
    const closeButton = messageEl.querySelector('button[aria-label="Close"]')
    closeButton.addEventListener('click', () => {
      messageEl.remove()
    })
    
    // Auto-remove after 4 seconds (longer for errors)
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.remove()
      }
    }, 4000)
  }
  
  async submitFormsAndClose() {
    let hasChanges = false
    
    // Submit timezone form if it exists and has changes
    const timezoneForm = document.getElementById('timezone-form')
    if (timezoneForm) {
      try {
        const formData = new FormData(timezoneForm)
        const response = await fetch(timezoneForm.action, {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to update timezone')
        }
        
        this.showSuccessMessage('Timezone updated successfully')
        hasChanges = true
      } catch (error) {
        console.error('Failed to update timezone:', error)
        this.showErrorMessage('Failed to update timezone')
        return
      }
    }
    
    // Submit date format form if it exists and has changes
    const dateFormatForm = document.getElementById('date-format-form')
    if (dateFormatForm) {
      try {
        const formData = new FormData(dateFormatForm)
        const response = await fetch(dateFormatForm.action, {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to update date format')
        }
        
        this.showSuccessMessage('Date format updated successfully')
        hasChanges = true
      } catch (error) {
        console.error('Failed to update date format:', error)
        this.showErrorMessage('Failed to update date format')
        return
      }
    }
    
    // Close modal
    this.hideModal()
    
    // Only reload if there were actual changes
    if (hasChanges) {
      // Small delay to let user see the success messages before reload
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    }
  }
}